import React, { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { Lock, CheckCircle, Briefcase } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { auth } from '../utils/api';
import { toast } from 'sonner';

const ResetPassword = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        if (password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        setLoading(true);
        try {
            await auth.resetPassword(token, password);
            setSuccess(true);
        } catch (err) {
            const msg = err.userMessage || 'Reset failed. The link may have expired.';
            toast.error(msg);
            if (err.response?.status === 400) {
                toast.error('Need a new link? Use the "Forgot password?" option on the login page.', { duration: 6000 });
            }
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F0FDF4' }}>
                <div className="bg-white rounded-3xl shadow-sm border border-red-200/50 max-w-md w-full p-8 text-center">
                    <h1 className="text-2xl font-bold mb-2 tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Invalid Reset Link</h1>
                    <p className="text-slate-500 mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>This password reset link is invalid or has expired.</p>
                    <Link to="/forgot-password">
                        <Button className="btn-primary">Request New Link</Button>
                    </Link>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F0FDF4' }}>
                <div className="bg-white rounded-3xl shadow-sm border border-purple-200/50 max-w-md w-full p-8 text-center">
                    <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6" style={{ backgroundColor: '#ECFDF5' }}>
                        <CheckCircle className="h-8 w-8 text-[#10B981]" />
                    </div>
                    <h1 className="text-2xl font-bold mb-2 tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }} data-testid="password-reset-success">
                        Password Reset
                    </h1>
                    <p className="text-slate-500 mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>Your password has been updated. You can now log in.</p>
                    <Button onClick={() => navigate('/login')} className="w-full btn-primary h-12" data-testid="go-to-login-btn">
                        Go to Login
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#F0FDF4' }}>
            <div className="bg-white rounded-3xl shadow-sm border border-purple-200/50 max-w-md w-full p-8">
                <div className="flex items-center space-x-2 mb-8 justify-center">
                    <img src="/sampler-logo.png" alt="Sampler" className="h-9 w-auto" />
                    <span className="text-xl font-bold" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }}>Sampler</span>
                </div>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#F3E8FF' }}>
                    <Lock className="h-6 w-6" style={{ color: '#9333EA' }} />
                </div>
                <span className="uppercase tracking-[0.2em] text-xs font-semibold text-[#8B5CF6] block mb-2 text-center" style={{ fontFamily: 'Unbounded, sans-serif' }}>
                    Security
                </span>
                <h1 className="text-2xl font-bold mb-2 text-center tracking-tight" style={{ fontFamily: 'Unbounded, sans-serif', color: '#0A0A0A' }} data-testid="reset-password-heading">
                    Set New Password
                </h1>
                <p className="text-slate-500 mb-6 text-center text-sm" style={{ fontFamily: 'Barlow, sans-serif' }}>
                    Choose a new password for your account.
                </p>
                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <Input type="password" placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} className="rounded-xl border-purple-200/50" data-testid="new-password-input" />
                    </div>
                    <div className="mb-4">
                        <Input type="password" placeholder="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className="rounded-xl border-purple-200/50" data-testid="confirm-password-input" />
                    </div>
                    <Button type="submit" disabled={loading || !password || !confirmPassword} className="w-full btn-primary h-12" data-testid="reset-password-submit-btn">
                        {loading ? 'Resetting...' : 'Reset Password'}
                    </Button>
                </form>
            </div>
        </div>
    );
};

export default ResetPassword;
